import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/helpers/toast/toast.helper'
import { UserAuthenticated } from '@/redux/user/types'
import {
  AUTH_SERVER_SESSION_REQUIRED,
  FIREBASE_PROJECT_MISMATCH,
  clearImpersonatedFirebaseAuth,
  endImpersonation,
  searchImpersonationUsers,
  startImpersonation
} from './service'
import {
  IMPERSONATION_BAR_COLLAPSED_KEY,
  IMPERSONATION_BAR_POSITION_KEY,
  IMPERSONATION_REOPEN_KEY,
  IMPERSONATION_STORAGE_KEY,
  ImpersonationSession,
  ImpersonationUser,
  SUPPORT_SUPERUSER_EMAIL
} from './types'
import { ImpersonationUserTable } from './ImpersonationUserTable'
import * as S from './styles'

function storedUser(): UserAuthenticated | null {
  try {
    return JSON.parse(localStorage.getItem('devchat_app_userAuthenticated') || 'null')
  } catch {
    return null
  }
}
function storedSession(): ImpersonationSession | null {
  try {
    return JSON.parse(localStorage.getItem(IMPERSONATION_STORAGE_KEY) || 'null')
  } catch {
    return null
  }
}
function formatRemaining(milliseconds: number) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000))
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

function isImpersonationToken(token?: string) {
  try {
    const encoded = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '='))
    return JSON.parse(payload).devzappImpersonation === true
  } catch {
    return false
  }
}

type BarPosition = { x: number; y: number }

function storedBarPosition(): BarPosition | null {
  try {
    const position = JSON.parse(localStorage.getItem(IMPERSONATION_BAR_POSITION_KEY) || 'null')
    return Number.isFinite(position?.x) && Number.isFinite(position?.y) ? position : null
  } catch {
    return null
  }
}

function visibleBarPosition(x: number, y: number, width: number, height: number): BarPosition {
  const margin = 8
  return {
    x: Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - width - margin)),
    y: Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - height - margin))
  }
}

export function ImpersonationControl() {
  const { t } = useTranslation()
  const tr = (key: string) => t(`translation:impersonation.${key}` as any)
  const [session, setSession] = useState<ImpersonationSession | null>(() => storedSession())
  const [now, setNow] = useState(Date.now())
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<ImpersonationUser[]>([])
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0, hasNext: false })
  const [selected, setSelected] = useState<ImpersonationUser | null>(null)
  const [campaignId, setCampaignId] = useState('')
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(IMPERSONATION_BAR_COLLAPSED_KEY) === 'true')
  const [barPosition, setBarPosition] = useState<BarPosition | null>(() => storedBarPosition())
  const collapsedBarRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null)
  const currentUser = storedUser()
  const originalUser = session?.originalUser || currentUser
  const originalAccessToken = originalUser?.accessToken || ''
  const hasOriginalUser = Boolean(originalUser)
  const allowed = originalUser?.email?.toLowerCase() === SUPPORT_SUPERUSER_EMAIL
  const remaining = useMemo(() => (session ? session.expiresAt - now : 0), [now, session])

  useEffect(() => {
    const syncSession = (event: StorageEvent) => {
      if (event.key === IMPERSONATION_STORAGE_KEY) setSession(storedSession())
    }
    window.addEventListener('storage', syncSession)
    return () => window.removeEventListener('storage', syncSession)
  }, [])

  useEffect(() => {
    function keepBarVisible() {
      setBarPosition(current => {
        if (!current || !collapsedBarRef.current) return current
        const next = visibleBarPosition(
          current.x,
          current.y,
          collapsedBarRef.current.offsetWidth,
          collapsedBarRef.current.offsetHeight
        )
        localStorage.setItem(IMPERSONATION_BAR_POSITION_KEY, JSON.stringify(next))
        return next
      })
    }
    const frame = collapsed ? window.requestAnimationFrame(keepBarVisible) : 0
    window.addEventListener('resize', keepBarVisible)
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', keepBarVisible)
    }
  }, [collapsed])

  useEffect(() => {
    if (!session && isImpersonationToken(currentUser?.accessToken)) {
      localStorage.removeItem('devchat_app_userAuthenticated')
      localStorage.removeItem('devchat_app_selectedCompanyId')
      localStorage.removeItem('devchat_app_selectedCompanyOwnerId')
      clearImpersonatedFirebaseAuth().finally(() => window.location.assign('/sign-in'))
    }
  }, [currentUser?.accessToken, session])

  useEffect(() => {
    if (!session) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [session])

  async function finish(reopenPicker = false) {
    if (!session) return
    setLoading(true)
    try {
      const actorAccessToken = await endImpersonation(session)
      const restoredUser = { ...session.originalUser, accessToken: actorAccessToken }
      localStorage.setItem('devchat_app_userAuthenticated', JSON.stringify(restoredUser))
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      localStorage.removeItem('devchat_app_selectedCompanyId')
      localStorage.removeItem('devchat_app_selectedCompanyOwnerId')
      if (reopenPicker) sessionStorage.setItem(IMPERSONATION_REOPEN_KEY, 'true')
      window.location.assign('/select-campaign')
    } catch {
      await clearImpersonatedFirebaseAuth()
      localStorage.removeItem('devchat_app_userAuthenticated')
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY)
      localStorage.removeItem('devchat_app_selectedCompanyId')
      localStorage.removeItem('devchat_app_selectedCompanyOwnerId')
      window.location.assign('/sign-in')
    }
  }

  useEffect(() => {
    if (session && remaining <= 0) finish().catch(() => undefined)
  }, [remaining, session])

  useEffect(() => {
    if (!open || !hasOriginalUser) return
    let active = true
    const timer = window.setTimeout(() => {
      setSearching(true)
      searchImpersonationUsers(originalAccessToken, query.trim(), pagination.page, pagination.pageSize)
        .then(result => {
          if (!active) return
          setUsers(result.users)
          setPagination(result.pagination)
          setSelected(current => {
            if (!current || result.users.some(user => user.id === current.id)) return current
            setCampaignId('')
            return null
          })
        })
        .catch(() => toast({ type: 'error', message: tr('searchError') }))
        .finally(() => active && setSearching(false))
    }, 350)
    return () => {
      active = false
      window.clearTimeout(timer)
    }
  }, [hasOriginalUser, open, originalAccessToken, pagination.page, pagination.pageSize, query])

  function openModal() {
    setQuery('')
    setUsers([])
    setSelected(null)
    setCampaignId('')
    setPagination({ page: 1, pageSize: 10, total: 0, hasNext: false })
    setOpen(true)
  }

  useEffect(() => {
    if (!allowed || session || sessionStorage.getItem(IMPERSONATION_REOPEN_KEY) !== 'true') return
    sessionStorage.removeItem(IMPERSONATION_REOPEN_KEY)
    openModal()
  }, [allowed, session])

  function setBarCollapsed(value: boolean) {
    setCollapsed(value)
    localStorage.setItem(IMPERSONATION_BAR_COLLAPSED_KEY, String(value))
  }

  function beginBarMove(event: React.PointerEvent<HTMLButtonElement>) {
    const bar = collapsedBarRef.current
    if (!bar) return
    const bounds = bar.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top
    }
    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
  }

  function moveBar(event: React.PointerEvent<HTMLButtonElement>) {
    const drag = dragRef.current
    const bar = collapsedBarRef.current
    if (!drag || drag.pointerId !== event.pointerId || !bar) return
    const next = visibleBarPosition(
      event.clientX - drag.offsetX,
      event.clientY - drag.offsetY,
      bar.offsetWidth,
      bar.offsetHeight
    )
    setBarPosition(next)
    localStorage.setItem(IMPERSONATION_BAR_POSITION_KEY, JSON.stringify(next))
  }

  function finishBarMove(event: React.PointerEvent<HTMLButtonElement>) {
    if (dragRef.current?.pointerId !== event.pointerId) return
    dragRef.current = null
  }

  function moveBarWithKeyboard(event: React.KeyboardEvent<HTMLButtonElement>) {
    const bar = collapsedBarRef.current
    const distance = event.shiftKey ? 60 : 20
    const direction = {
      ArrowLeft: [-distance, 0],
      ArrowRight: [distance, 0],
      ArrowUp: [0, -distance],
      ArrowDown: [0, distance]
    }[event.key]
    if (!bar || !direction) return
    const bounds = bar.getBoundingClientRect()
    const next = visibleBarPosition(
      bounds.left + direction[0],
      bounds.top + direction[1],
      bar.offsetWidth,
      bar.offsetHeight
    )
    setBarPosition(next)
    localStorage.setItem(IMPERSONATION_BAR_POSITION_KEY, JSON.stringify(next))
    event.preventDefault()
  }

  async function start() {
    if (!originalUser || !selected || !campaignId) return
    setLoading(true)
    try {
      if (session) throw new Error('active_impersonation_must_be_ended')
      const selectedCampaign = selected.campaigns.find(item => item.id === campaignId)
      const companyId = selectedCampaign?.companyId || campaignId
      const company = selected.companies?.find(item => item.id === companyId) || selected.companies?.[0]
      if (!company) throw new Error('target_company_not_found')
      const result = await startImpersonation(originalUser, selected, campaignId)
      const resolvedTarget = result.firebase.user
      const resolvedCompany = resolvedTarget.companies?.find(item => item.id === companyId) || company
      const restoredOriginalUser = { ...originalUser, accessToken: result.actorFirebaseToken }
      const simulatedUser: UserAuthenticated = {
        ...resolvedTarget,
        ownerId: resolvedCompany?.ownerId || resolvedTarget.ownerId || resolvedTarget.id,
        token: result.oauth.access_token,
        accessToken: result.firebaseIdToken,
        role: resolvedCompany?.role?.[0] || null,
        isAdmin: Boolean(resolvedCompany?.role?.some(role => role === 'MASTER' || role === 'ADMIN')),
        isWork: true
      } as UserAuthenticated
      const nextSession: ImpersonationSession = {
        firebaseSessionId: result.firebase.session.id,
        oauthSessionId: result.oauth.session_id,
        actorRestoreCustomToken: result.firebase.actorRestoreCustomToken,
        expiresAt: Math.min(result.firebase.session.expiresAt, Date.now() + result.oauth.expires_in * 1000),
        originalUser: restoredOriginalUser,
        simulatedUser,
        simulatedName: selected.name,
        simulatedEmail: selected.email,
        campaignId,
        companyId: resolvedCompany.id
      }
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(nextSession))
      localStorage.setItem('devchat_app_userAuthenticated', JSON.stringify(simulatedUser))
      localStorage.setItem('devchat_app_selectedCompanyId', resolvedCompany.id)
      localStorage.setItem('devchat_app_selectedCompanyOwnerId', simulatedUser.ownerId || '')
      window.location.assign(`/chat-page?companyId=${encodeURIComponent(resolvedCompany.id)}`)
    } catch (error) {
      const errorCode = error instanceof Error ? error.message : ''
      const message =
        errorCode === AUTH_SERVER_SESSION_REQUIRED
          ? tr('authSessionRequired')
          : errorCode === FIREBASE_PROJECT_MISMATCH
          ? tr('firebaseProjectMismatch')
          : tr('startError')
      toast({ type: 'error', message })
      setLoading(false)
    }
  }

  if (!allowed && !session) return null
  return (
    <>
      {collapsed ? (
        <S.CollapsedBar
          ref={collapsedBarRef}
          style={barPosition ? { left: barPosition.x, top: barPosition.y, right: 'auto' } : undefined}
        >
          <S.DragHandle
            type="button"
            onPointerDown={beginBarMove}
            onPointerMove={moveBar}
            onPointerUp={finishBarMove}
            onPointerCancel={finishBarMove}
            onKeyDown={moveBarWithKeyboard}
            title={tr('moveBar')}
            aria-label={tr('moveBar')}
          >
            ⠿
          </S.DragHandle>
          {session && <S.Countdown>{formatRemaining(remaining)}</S.Countdown>}
          <S.ToggleButton onClick={() => setBarCollapsed(false)} title={tr('showBar')} aria-label={tr('showBar')}>
            {tr('simulation')} ▾
          </S.ToggleButton>
        </S.CollapsedBar>
      ) : (
        <S.Bar>
          <span>{session ? `${tr('activeAs')} ${session.simulatedName}` : tr('supportAccess')}</span>
          <S.Actions>
            {session && <S.Countdown>{formatRemaining(remaining)}</S.Countdown>}
            <S.Button
              onClick={async () => {
                if (session) await finish(true)
                else openModal()
              }}
              disabled={loading}
            >
              {session ? tr('switchUser') : tr('start')}
            </S.Button>
            {session && (
              <S.Button
                $primary
                onClick={async () => {
                  await finish()
                }}
                disabled={loading}
              >
                {tr('end')}
              </S.Button>
            )}
            <S.ToggleButton onClick={() => setBarCollapsed(true)} title={tr('hideBar')} aria-label={tr('hideBar')}>
              ▴
            </S.ToggleButton>
          </S.Actions>
        </S.Bar>
      )}
      {open && (
        <S.Backdrop role="presentation" onMouseDown={event => event.target === event.currentTarget && setOpen(false)}>
          <S.Modal role="dialog" aria-modal="true">
            <S.ModalHeader>
              <S.Title>{tr('title')}</S.Title>
              <S.Description>{tr('description')}</S.Description>
            </S.ModalHeader>
            <S.ModalBody>
              <S.Input
                value={query}
                onChange={event => {
                  setQuery(event.target.value)
                  setPagination(current => ({ ...current, page: 1 }))
                }}
                placeholder={tr('searchPlaceholder')}
              />
              <ImpersonationUserTable
                users={users}
                selectedId={selected?.id}
                pagination={pagination}
                loading={searching}
                onPageChange={page => setPagination(current => ({ ...current, page }))}
                onSelect={user => {
                  setSelected(user)
                  setCampaignId(user.campaigns?.[0]?.id || '')
                }}
              />
              {selected && (
                <S.FieldGroup>
                  <S.FieldLabel htmlFor="impersonation-campaign">{tr('campaignSelection')}</S.FieldLabel>
                  <S.Select
                    id="impersonation-campaign"
                    value={campaignId}
                    onChange={event => setCampaignId(event.target.value)}
                  >
                    <option value="">{tr('selectCampaign')}</option>
                    {selected.campaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </option>
                    ))}
                  </S.Select>
                </S.FieldGroup>
              )}
            </S.ModalBody>
            <S.Footer>
              <S.Button onClick={() => setOpen(false)}>{tr('cancel')}</S.Button>
              <S.Button
                $primary
                disabled={!selected || !campaignId || loading}
                onClick={async () => {
                  await start()
                }}
              >
                {loading ? tr('loading') : tr('confirm')}
              </S.Button>
            </S.Footer>
          </S.Modal>
        </S.Backdrop>
      )}
    </>
  )
}
